/**
 * Matches a single `var()` reference and splits it into the custom property
 * name and everything after the first comma (its fallback).
 *
 * Anchored to the end of the string so the closing paren of a NESTED reference
 * — `var(--a, var(--b))` — is attributed to the outer one, leaving the inner
 * reference intact in the fallback group for the recursive step below.
 */
const VAR_REFERENCE = /^\s*var\(\s*(--[A-Za-z0-9_-]+)\s*(?:,([\s\S]*))?\)\s*$/;

/**
 * Resolve a stored setting value to a concrete CSS value.
 *
 * Non-`var()` input is returned untouched — including the `"design-system"`
 * sentinel, which is not a colour. Callers that may hold the sentinel have to
 * map it onto the slot's CSS variable before calling this (see
 * `button-colors.tsx`), otherwise it comes back verbatim and lands in the UI as
 * the literal text "design-system".
 *
 * `var()` input is resolved against `container` (defaulting to `document.body`,
 * which inherits the design system written to `:root`). Fallbacks are followed
 * recursively, so `var(--a, var(--b))` yields `--b` when `--a` is unset. The
 * elements-bar button presets ship exactly that shape, and the previous
 * single-group regex captured `"--a, var(--b"` as the property name, resolved
 * nothing, and left the settings panel blank next to a correctly rendered
 * button.
 */
export const getDesignSystemVariable = (
  variable: string,
  container?: HTMLElement | null,
): string => {
  if (typeof variable !== "string" || !variable?.includes("var")) {
    return variable;
  }

  const styles = getComputedStyle(container || document.body);

  const resolve = (reference: string, depth: number): string => {
    // A malformed or absurdly nested value resolves to nothing rather than
    // recursing without bound.
    if (depth > 10) return "";

    const match = reference.match(VAR_REFERENCE);
    // Not a var() reference — this is the literal fallback of an outer one
    // (e.g. the `#ABCDEF` in `var(--unset, #ABCDEF)`).
    if (!match) return reference.trim();

    const [, name, fallback] = match;
    const value = styles.getPropertyValue(name).trim();
    if (value) return value;
    if (fallback?.trim()) return resolve(fallback, depth + 1);
    return "";
  };

  return resolve(variable, 0);
};
